import { useState, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { SEARCH_COHORT_ENTRIES } from '~/graphql/queries';
import type {
  SearchCohortEntriesQuery,
  SearchCohortEntriesInput,
} from '~/graphql/gen/graphql';
import { SENTRY_OPERATIONS } from '~/shared/constants';
import { reportError } from '~/shared/utils/sentry';
import AppHeader from '~/shared/components/AppHeader';
import FilterPanel from '~/modules/sponsor/components/FilterPanel';
import SponsorDashboardLoadingState from '~/modules/sponsor/components/SponsorDashboardLoadingState';
import SponsorDashboardPreSearchState from '~/modules/sponsor/components/SponsorDashboardPreSearchState';
import SponsorDashboardInsufficientDataState from '~/modules/sponsor/components/SponsorDashboardInsufficientDataState';
import SponsorDashboardResultsState from '~/modules/sponsor/components/SponsorDashboardResultsState';

const SponsorDashboard = () => {
  const [filters, setFilters] = useState<SearchCohortEntriesInput>({});
  const [resetKey, setResetKey] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);

  const [executeSearch, { data, loading: isSearching }] =
    useLazyQuery<SearchCohortEntriesQuery>(SEARCH_COHORT_ENTRIES, {
      fetchPolicy: 'network-only',
    });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const latestRequestIdRef = useRef(0);

  const hasActiveFilters = Object.keys(filters).length > 0;

  useEffect(() => {
    if (!hasActiveFilters) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
      return;
    }
    latestRequestIdRef.current += 1;
    debounceRef.current = setTimeout(async () => {
      const requestId = ++latestRequestIdRef.current;
      setSearchError(undefined);
      setHasSearched(false);
      try {
        const { error } = await executeSearch({
          variables: { input: filters },
        });
        if (requestId !== latestRequestIdRef.current) return;
        if (error) {
          setSearchError(error.message);
          setHasSearched(false);
        } else {
          setHasSearched(true);
        }
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return;
        const errorMessage =
          err instanceof Error ? err.message : 'Search failed';
        setSearchError(errorMessage);
        setHasSearched(false);
        reportError(err, {
          operation: SENTRY_OPERATIONS.SPONSOR_COHORT_SEARCH,
        });
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters, executeSearch, hasActiveFilters]);

  const handleClearFilters = () => {
    latestRequestIdRef.current += 1;
    setFilters({});
    setResetKey((k) => k + 1);
    setHasSearched(false);
    setSearchError(undefined);
  };

  const result = data?.searchCohortEntries;
  const filtersApplied = result?.filtersApplied ?? 0;

  const showPreSearch = !isSearching && !hasActiveFilters;
  const showLoading = isSearching && hasActiveFilters;
  const showInsufficientData =
    hasActiveFilters &&
    !isSearching &&
    hasSearched &&
    result &&
    filtersApplied > 0 &&
    result.totalCohortSize === '<11 (suppressed)';
  const showResults =
    hasActiveFilters &&
    !isSearching &&
    hasSearched &&
    result &&
    filtersApplied > 0 &&
    result.totalCohortSize !== '<11 (suppressed)';

  return (
    <DashboardContainer>
      <AppHeader />
      <ContentRow>
        <FilterScroll>
          <FilterPanel
            key={resetKey}
            onChange={setFilters}
            onClear={handleClearFilters}
          />
        </FilterScroll>
        <MainScroll>
          <MainContent>
            <MainStack>
              {searchError && <Alert severity="error">{searchError}</Alert>}

              {/* Loading state */}
              {showLoading && <SponsorDashboardLoadingState />}

              {/* Pre-search state (no filters applied) */}
              {showPreSearch && <SponsorDashboardPreSearchState />}

              {/* Insufficient data state (cohort size suppressed) */}
              {showInsufficientData && (
                <SponsorDashboardInsufficientDataState
                  onClearFilters={handleClearFilters}
                />
              )}

              {/* Results state (filters applied, cohort size visible) */}
              {showResults && result && (
                <SponsorDashboardResultsState
                  result={result}
                  onClearFilters={handleClearFilters}
                />
              )}
            </MainStack>
          </MainContent>
        </MainScroll>
      </ContentRow>
    </DashboardContainer>
  );
};

export default SponsorDashboard;

const DashboardContainer = styled(Stack)(() => ({
  width: '100%',
  height: '100vh',
  overflow: 'hidden',
  backgroundColor: theme.palette.custom.sponsorPageBackground,
}));

const MainContent = styled(Stack)(() => ({
  flex: 1,
  padding: theme.spacing(20),
  overflowY: 'auto',
}));

const ContentRow = styled(Stack)(() => ({
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
}));

const FilterScroll = styled(Stack)(() => ({
  width: theme.tokens.layout.filterPanelWidth,
  flexShrink: 0,
  height: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const MainScroll = styled(Stack)(() => ({
  flex: 1,
  overflow: 'auto',
  minWidth: 0,
}));

const MainStack = styled(Stack)(() => ({
  gap: theme.spacing(4),
}));
